import { LoadsProps } from '@/common/models/ILoads';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface LoadsPopoverProps extends LoadsProps {}

export const LoadsPopover = ({
  items,
  selectedChips,
  onToggle,
  onSelectAll,
  viewBtnClassName,
}: LoadsPopoverProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectAllRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const allSelected = items.every((item) =>
    selectedChips.some((el) => el.id === item.id),
  );
  const someSelected =
    items.some((item) => selectedChips.some((el) => el.id === item.id)) &&
    !allSelected;

  useEffect(() => {
    if (!selectAllRef.current) return;
    const input = selectAllRef.current.querySelector(
      'input[type="checkbox"]',
    ) as HTMLInputElement | null;
    if (input) {
      input.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleSelectAll = () => {
    onSelectAll(allSelected ? [] : items);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!scrollRef.current) return;
    e.stopPropagation();
    scrollRef.current.scrollTop += e.deltaY;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'px-4 py-1.5 rounded-full text-sm font-medium text-gray-500 hover:border-[#47AA62] hover:text-[#47AA62] bg-white transition-all cursor-pointer',
            viewBtnClassName,
          )}
        >
          {t('view.all')}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-64 p-0 shadow-lg rounded-xl border border-gray-200"
        onWheel={handleWheel}
      >
        <div className="flex flex-col">
          <label
            htmlFor="select-all"
            className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer"
          >
            <Checkbox
              id="select-all"
              ref={selectAllRef}
              checked={allSelected}
              onCheckedChange={handleSelectAll}
            />
            <span className="text-sm font-medium select-none">
              {t('select.all')}
            </span>
          </label>

          <div
            ref={scrollRef}
            className="flex flex-col max-h-[280px] overflow-y-auto py-1"
          >
            {items.map((item) => (
              <label
                key={item.id}
                htmlFor={`chip-${item.id}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <Checkbox
                  id={`chip-${item.id}`}
                  checked={selectedChips.some((el) => el.id === item.id)}
                  onCheckedChange={() => onToggle(item)}
                />
                <span className="text-sm font-medium select-none">
                  {item.number}
                </span>
              </label>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
